import React, { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BanknotesIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  CloudArrowUpIcon,
  CubeIcon,
  DocumentMagnifyingGlassIcon,
  MagnifyingGlassIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import SEO from '../components/SEO';
import { useAppContext } from '../context/AppContext';
import { HomePageHeader } from './HomePageNew';
import { trackGoal } from '../utils/metrika';

export interface ProductPainSeoPageData {
  path: string;
  title: string;
  description: string;
  keywords: string;
  eyebrow: string;
  h1: string;
  intro: string;
  searchIntent: string;
  manualSearch: string[];
  parameters: string[];
  examples: Array<{
    source: string;
    parsed: string;
    result: string;
  }>;
  outcomes: string[];
  faq: Array<{
    question: string;
    answer: string;
  }>;
}

export const productPainPages: ProductPainSeoPageData[] = [
  {
    path: '/podbor-avtomaticheskih-vyklyuchateley',
    title: 'Подбор автоматических выключателей по спецификации - Get-Goods.ai',
    description:
      'Get-Goods.ai подбирает автоматические выключатели по спецификации: полюса, номинал, характеристика, отключающая способность, бренд, артикул и цена.',
    keywords:
      'подбор автоматических выключателей, автомат по спецификации, автомат 3P C25, подбор автомата по номиналу, артикулы автоматических выключателей',
    eyebrow: 'Автоматические выключатели',
    h1: 'Подбор автоматических выключателей по спецификации',
    intro:
      'Get-Goods.ai разбирает строки спецификации с автоматами и помогает найти реальные изделия: номинал, количество полюсов, характеристику отключения, отключающую способность, бренд, артикул и цену.',
    searchIntent:
      'Обычно приходится искать конкретный автомат в каталогах поставщиков, сверять характеристики вручную и проверять каждый важный параметр из проектной строки.',
    manualSearch: [
      'автомат 3P C25 6кА артикул',
      'ВА47-29 C16 1P цена',
      'автоматический выключатель C32 3 полюса',
      'автомат по спецификации ЭОМ'
    ],
    parameters: [
      'количество полюсов: 1P, 2P, 3P, 4P',
      'номинальный ток и характеристика: B, C, D',
      'отключающая способность: 4.5 кА, 6 кА, 10 кА',
      'серия, бренд, монтаж и требования проекта',
      'количество и единицы измерения'
    ],
    examples: [
      {
        source: 'Авт. выкл. 3P C25 6кА',
        parsed: '3 полюса, C25, отключающая способность 6 кА',
        result: 'бренд, артикул производителя, цена'
      },
      {
        source: 'ВА47-29 1P C16',
        parsed: 'серия ВА47-29, 1 полюс, C16',
        result: 'подходящие позиции из каталога'
      },
      {
        source: 'QF вводной 4P C63',
        parsed: 'вводной аппарат, 4P, C63',
        result: 'варианты под требования щита'
      }
    ],
    outcomes: [
      'быстрее найти артикул по строке спецификации',
      'не потерять полюсность, характеристику и отключающую способность',
      'сравнить подходящие бренды и цены из доступных источников',
      'подготовить результат для закупки или проверки спецификации'
    ],
    faq: [
      {
        question: 'Можно ли подобрать автомат, если в строке нет артикула?',
        answer:
          'Да. Get-Goods.ai использует технические признаки из строки: номинал, количество полюсов, характеристику, отключающую способность и требования проекта.'
      },
      {
        question: 'Что делать, если в спецификации указан только тип автомата?',
        answer:
          'Сервис выделяет доступные параметры и показывает, какие данные нужно уточнить для уверенного подбора.'
      }
    ]
  },
  {
    path: '/podbor-kabelya-po-specifikacii',
    title: 'Подбор кабеля по спецификации и проекту - Get-Goods.ai',
    description:
      'Get-Goods.ai помогает подобрать кабель по спецификации: марка, сечение, число жил, исполнение, длина, бренд, артикул и цена.',
    keywords:
      'подбор кабеля по спецификации, ВВГнг LS 5х10, кабель по проекту, артикул кабеля, цена кабеля по спецификации',
    eyebrow: 'Кабель и провод',
    h1: 'Подбор кабеля по спецификации и проекту',
    intro:
      'Get-Goods.ai извлекает марку кабеля, исполнение, количество жил, сечение и длину из спецификаций и проектных таблиц, чтобы быстрее перейти к артикулам, брендам и ценам.',
    searchIntent:
      'При ручном поиске кабеля пользователи часто перебирают каталоги по марке и сечению, а затем отдельно сверяют исполнение, пожарные требования и длину.',
    manualSearch: [
      'ВВГнг(A)-LS 5х10 цена',
      'кабель КГ 3х2.5 артикул',
      'подобрать кабель по проекту',
      'кабельная спецификация закупка'
    ],
    parameters: [
      'марка: ВВГнг(A)-LS, КГ, ПВС, NYM и другие',
      'число жил и сечение',
      'исполнение: LS, FRLS, HF, экранирование',
      'длина, единицы измерения и запас',
      'требования проекта и предпочтения по брендам'
    ],
    examples: [
      {
        source: 'ВВГнг(A)-LS 5х10, 120 м',
        parsed: 'марка ВВГнг(A)-LS, 5 жил, 10 мм2, длина 120 м',
        result: 'позиции производителей и цены'
      },
      {
        source: 'КГ 3х2.5',
        parsed: 'гибкий кабель, 3 жилы, 2.5 мм2',
        result: 'артикулы и варианты поставки'
      },
      {
        source: 'ПВС 3х1.5 белый',
        parsed: 'провод ПВС, 3 жилы, 1.5 мм2, цвет',
        result: 'каталожные позиции'
      }
    ],
    outcomes: [
      'сократить ручной поиск по марке и сечению',
      'проверить исполнение кабеля до запроса цены',
      'собрать закупочный список с количеством и длиной',
      'видеть источники цен, когда они доступны'
    ],
    faq: [
      {
        question: 'Можно ли обрабатывать кабельные позиции из PDF?',
        answer:
          'Да. Если позиции доступны в спецификации или таблице проектной документации, Get-Goods.ai извлекает марку, сечение, количество и длину.'
      },
      {
        question: 'Учитывается ли пожарное исполнение кабеля?',
        answer:
          'Да. Исполнения LS, FRLS, HF и похожие признаки используются как часть технического подбора.'
      }
    ]
  },
  {
    path: '/podbor-kabelnyh-lotkov',
    title: 'Подбор кабельных лотков и аксессуаров по спецификации - Get-Goods.ai',
    description:
      'Get-Goods.ai подбирает кабельные лотки по спецификации: тип, ширина, высота, покрытие, аксессуары, бренд, артикул и цена.',
    keywords:
      'подбор кабельных лотков, лоток кабельный по спецификации, лоток перфорированный артикул, аксессуары кабельных лотков',
    eyebrow: 'Кабельные лотки',
    h1: 'Подбор кабельных лотков и аксессуаров',
    intro:
      'Get-Goods.ai помогает разбирать позиции по кабельным лоткам: тип лотка, ширину, высоту, длину, покрытие, толщину металла и аксессуары для монтажа.',
    searchIntent:
      'Лотки часто ищут вручную по размеру и типу, затем отдельно подбирают крышки, соединители, углы, подвесы и крепеж.',
    manualSearch: [
      'лоток перфорированный 100х50 цена',
      'кабельный лоток 200х50 артикул',
      'крышка лотка 100 мм',
      'аксессуары для кабельных лотков'
    ],
    parameters: [
      'тип: перфорированный, неперфорированный, лестничный, проволочный',
      'ширина, высота, длина и толщина',
      'покрытие и материал',
      'крышки, соединители, углы и крепеж',
      'количество, метраж и комплектность'
    ],
    examples: [
      {
        source: 'Лоток перф. 100х50 L=3000 оц.',
        parsed: 'перфорированный лоток, 100х50, длина 3000, оцинковка',
        result: 'артикул лотка и цена'
      },
      {
        source: 'Крышка лотка 100 мм',
        parsed: 'крышка для лотка шириной 100 мм',
        result: 'совместимые позиции'
      },
      {
        source: 'Угол горизонтальный 90 гр. 200х50',
        parsed: 'аксессуар, угол 90 градусов, 200х50',
        result: 'каталожная позиция'
      }
    ],
    outcomes: [
      'собрать комплект по трассе и спецификации',
      'не забыть аксессуары и крепеж',
      'быстрее перейти от размера к артикулу',
      'подготовить список для запроса цены'
    ],
    faq: [
      {
        question: 'Можно ли подобрать аксессуары к лоткам?',
        answer:
          'Да. Get-Goods.ai выделяет крышки, соединители, углы, подвесы и другие аксессуары как отдельные позиции спецификации.'
      },
      {
        question: 'Учитывается ли покрытие лотка?',
        answer:
          'Да. Оцинковка, материал и другие признаки используются при сопоставлении с каталогом.'
      }
    ]
  },
  {
    path: '/podbor-shchitov-i-shkafov',
    title: 'Подбор электрических щитов и шкафов по спецификации - Get-Goods.ai',
    description:
      'Get-Goods.ai подбирает электрические щиты и шкафы по спецификации: тип, габариты, IP, монтаж, бренд, артикул и цена.',
    keywords:
      'подбор электрических щитов, щит распределительный по спецификации, шкаф электрический IP54, артикул щита',
    eyebrow: 'Щиты и шкафы',
    h1: 'Подбор электрических щитов и шкафов',
    intro:
      'Get-Goods.ai разбирает требования к распределительным щитам и шкафам: тип корпуса, габариты, степень защиты, способ монтажа, материал и комплектацию.',
    searchIntent:
      'При ручном подборе щитов приходится сверять габариты, IP, монтаж, количество модулей, материал корпуса и совместимость с проектом.',
    manualSearch: [
      'щит распределительный IP54 навесной',
      'шкаф электрический 600х800х250 цена',
      'щит на 36 модулей артикул',
      'подбор щита по спецификации'
    ],
    parameters: [
      'тип: щит, шкаф, корпус, бокс',
      'габариты и количество модулей',
      'степень защиты IP',
      'навесной или напольный монтаж',
      'материал, комплектация и вводы'
    ],
    examples: [
      {
        source: 'ЩРН-36 IP54 навесной',
        parsed: 'распределительный щит, 36 модулей, IP54, навесной',
        result: 'бренд, артикул и цена'
      },
      {
        source: 'Шкаф 600х800х250 IP65',
        parsed: 'шкаф, габариты 600х800х250, IP65',
        result: 'подходящие корпуса'
      },
      {
        source: 'Бокс пластиковый 12 мод.',
        parsed: 'пластиковый бокс, 12 модулей',
        result: 'каталожные позиции'
      }
    ],
    outcomes: [
      'быстрее подобрать корпус по техническим требованиям',
      'снизить риск ошибки по IP и габаритам',
      'сравнить бренды и цены по доступным данным',
      'получить список позиций для закупки'
    ],
    faq: [
      {
        question: 'Можно ли подобрать щит без точного артикула?',
        answer:
          'Да. Для подбора используются габариты, количество модулей, степень защиты, монтаж и материал.'
      },
      {
        question: 'Работает ли подбор по обозначениям из проекта?',
        answer:
          'Да. Обозначения щитов и комментарии используются вместе с техническими параметрами из спецификации.'
      }
    ]
  },
  {
    path: '/podbor-uzo-difavtomatov-kontaktorov',
    title: 'Подбор УЗО, дифавтоматов и контакторов по спецификации - Get-Goods.ai',
    description:
      'Get-Goods.ai подбирает УЗО, дифавтоматы, контакторы и модульную аппаратуру по спецификации: параметры, бренд, артикул и цена.',
    keywords:
      'подбор УЗО, подбор дифавтоматов, подбор контакторов, модульная аппаратура по спецификации, артикулы электротехники',
    eyebrow: 'Модульная аппаратура',
    h1: 'Подбор УЗО, дифавтоматов и контакторов',
    intro:
      'Get-Goods.ai выделяет параметры модульной аппаратуры из спецификаций: ток, полюса, чувствительность, тип, напряжение катушки, категория применения и количество.',
    searchIntent:
      'Такие позиции сложно искать вручную, потому что одна строка может содержать сразу несколько критичных параметров, влияющих на артикул.',
    manualSearch: [
      'УЗО 2P 40А 30мА тип А',
      'дифавтомат C16 30мА 1P+N',
      'контактор 25А катушка 220В',
      'модульная аппаратура по спецификации'
    ],
    parameters: [
      'для УЗО: полюса, ток, ток утечки, тип',
      'для дифавтоматов: характеристика, ток, утечка, полюса',
      'для контакторов: ток, напряжение катушки, контакты',
      'серия, бренд и монтаж',
      'количество и единицы измерения'
    ],
    examples: [
      {
        source: 'УЗО 2P 40А 30мА тип А',
        parsed: '2 полюса, 40 А, 30 мА, тип А',
        result: 'артикулы УЗО'
      },
      {
        source: 'Диф. авт. C16 30мА 1P+N',
        parsed: 'дифавтомат, C16, 30 мА, 1P+N',
        result: 'позиции модульной аппаратуры'
      },
      {
        source: 'Контактор 25А кат. 230В',
        parsed: 'контактор 25 А, катушка 230 В',
        result: 'бренд, артикул и цена'
      }
    ],
    outcomes: [
      'выделить параметры, которые влияют на артикул',
      'ускорить ручной поиск модульной аппаратуры',
      'проверить спорные позиции до закупки',
      'получить список товаров по спецификации'
    ],
    faq: [
      {
        question: 'Поддерживаются ли УЗО и дифавтоматы в одной спецификации?',
        answer:
          'Да. Get-Goods.ai разбирает разные типы модульной аппаратуры в одном файле и подбирает позиции по параметрам каждой строки.'
      },
      {
        question: 'Можно ли учитывать напряжение катушки контактора?',
        answer:
          'Да. Напряжение катушки, ток и конфигурация контактов используются при подборе контакторов.'
      }
    ]
  },
  {
    path: '/poisk-artikulov-po-specifikacii',
    title: 'Поиск артикулов электротехники по строке спецификации - Get-Goods.ai',
    description:
      'Get-Goods.ai помогает найти артикулы электротехнической продукции по строкам спецификации, техническим параметрам, брендам и доступным ценам.',
    keywords:
      'поиск артикулов по спецификации, найти артикул электротехники, артикул по описанию, подбор товара по строке спецификации',
    eyebrow: 'Поиск артикулов',
    h1: 'Поиск артикулов по строке спецификации',
    intro:
      'Когда в проекте есть описание без артикула, Get-Goods.ai помогает разобрать строку, выделить технические признаки и перейти к реальным товарам, брендам и ценам.',
    searchIntent:
      'Часто строку из Excel, PDF или проекта приходится копировать в поисковики, каталоги поставщиков и прайс-листы, а затем вручную сверять характеристики.',
    manualSearch: [
      'найти артикул по описанию электротехника',
      'поиск товара по спецификации',
      'артикул по строке ВА47 C25 3P',
      'подбор продукции из Excel спецификации'
    ],
    parameters: [
      'наименование и тип изделия',
      'ключевые технические характеристики',
      'бренд, серия и ограничения проекта',
      'количество, единица измерения и комментарии',
      'цена и источник данных, когда доступны'
    ],
    examples: [
      {
        source: 'Авт. выкл. 3P C25 6кА, 12 шт',
        parsed: 'автоматический выключатель, 3P, C25, 6 кА, 12 шт',
        result: 'артикулы и цены'
      },
      {
        source: 'Лоток кабельный перф. 100х50 оц.',
        parsed: 'кабельный лоток, перфорированный, 100х50, оцинкованный',
        result: 'каталожные позиции'
      },
      {
        source: 'Щит навесной IP54 36 мод.',
        parsed: 'навесной щит, IP54, 36 модулей',
        result: 'бренды и артикулы'
      }
    ],
    outcomes: [
      'не искать каждую строку вручную в нескольких каталогах',
      'получить структурированный результат по позициям',
      'видеть, какие параметры были распознаны',
      'переходить от спецификации к закупочному списку'
    ],
    faq: [
      {
        question: 'Можно ли искать артикулы по Excel-спецификации?',
        answer:
          'Да. Get-Goods.ai рассчитан на работу со спецификациями и перечнями номенклатуры, где много строк нужно сопоставить с реальными товарами.'
      },
      {
        question: 'Как это помогает при работе с каталогами поставщиков?',
        answer:
          'Сервис сокращает ручную работу: разбирает строки, подбирает позиции и показывает данные, которые можно использовать для проверки и закупки.'
      }
    ]
  }
];

const workflow = [
  {
    icon: CloudArrowUpIcon,
    title: 'Загрузите спецификацию',
    text: 'Файл, PDF, таблица или перечень номенклатуры становятся исходной точкой для подбора.'
  },
  {
    icon: DocumentMagnifyingGlassIcon,
    title: 'ИИ разбирает строки',
    text: 'Система выделяет тип изделия, параметры, количество, бренды и комментарии из проектной строки.'
  },
  {
    icon: CubeIcon,
    title: 'Подбирает товары',
    text: 'Позиции сопоставляются с реальными изделиями, артикулами производителей и доступными данными о цене.'
  },
  {
    icon: BanknotesIcon,
    title: 'Готовит результат',
    text: 'Результат удобно использовать для проверки спецификации, запроса цен и подготовки закупки.'
  }
];

const ProductPainSeoPage: React.FC<{ page: ProductPainSeoPageData }> = ({ page }) => {
  const { isAuthenticated } = useAppContext();
  const [query, setQuery] = useState(page.examples[0]?.source || '');

  const targetPath = isAuthenticated ? '/my-applications' : '/login';
  const trackSeoClick = (source: string) => {
    trackGoal('seo_page_click', {
      source,
      seo_path: page.path,
      destination: targetPath,
      authenticated: isAuthenticated,
    });
  };
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: page.h1,
      url: `https://get-goods.ai${page.path}`,
      description: page.description
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: page.faq.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }))
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Get-Goods.ai',
          item: 'https://get-goods.ai/'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: page.h1,
          item: `https://get-goods.ai${page.path}`
        }
      ]
    }
  ];

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    trackSeoClick('product_pain_form');
    window.location.href = targetPath;
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={page.title}
        description={page.description}
        canonicalPath={page.path}
        imagePath="/logo512.png"
        keywords={page.keywords}
        jsonLd={jsonLd}
      />
      <HomePageHeader />

      <main className="pt-20">
        <section className="bg-white">
          <div className="container mx-auto px-6 py-16 lg:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-blue-600 mb-4">
                  {page.eyebrow}
                </p>
                <h1 className="text-4xl md:text-6xl font-extrabold leading-tight text-gray-900 mb-6">
                  {page.h1}
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed mb-8">
                  {page.intro}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to={targetPath}
                    onClick={() => trackSeoClick('product_pain_hero')}
                    className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-7 py-4 rounded-lg text-base font-semibold transition-colors duration-300 shadow-lg"
                  >
                    {isAuthenticated ? (
                      <CloudArrowUpIcon className="h-6 w-6 mr-2" />
                    ) : (
                      <SparklesIcon className="h-6 w-6 mr-2" />
                    )}
                    {isAuthenticated ? 'Загрузить спецификацию' : 'Начать подбор'}
                  </Link>
                  <a
                    href="#examples"
                    className="inline-flex items-center justify-center border border-gray-300 hover:border-blue-500 text-gray-900 px-7 py-4 rounded-lg text-base font-semibold transition-colors duration-300"
                  >
                    Примеры позиций
                  </a>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg bg-gray-50 shadow-xl overflow-hidden">
                <div className="bg-gray-900 text-white px-5 py-4 flex items-center justify-between">
                  <div className="font-semibold">Проверить строку спецификации</div>
                  <MagnifyingGlassIcon className="h-6 w-6 text-blue-300" />
                </div>
                <form onSubmit={handleSubmit} className="p-5 bg-white">
                  <label htmlFor={`query-${page.path}`} className="block text-sm text-gray-600 mb-2">
                    Строка из спецификации
                  </label>
                  <textarea
                    id={`query-${page.path}`}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    className="w-full h-28 rounded-lg border border-gray-300 p-3 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                  <button
                    type="submit"
                    className="mt-4 inline-flex w-full items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-lg font-semibold transition-colors duration-300"
                  >
                    <TagIcon className="h-5 w-5 mr-2" />
                    Подобрать артикулы и цены
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mb-12">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
                Где обычно теряется время
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {page.searchIntent}
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-5">С чего начинается подбор</h3>
                <div className="space-y-3">
                  {page.manualSearch.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <MagnifyingGlassIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-5">Параметры для подбора</h3>
                <div className="space-y-3">
                  {page.parameters.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="examples" className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="flex items-center gap-3 mb-10">
              <ClipboardDocumentListIcon className="h-10 w-10 text-blue-600" />
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900">
                Примеры строк и результата
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {page.examples.map((item) => (
                <div key={item.source} className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
                  <div className="bg-gray-900 text-white px-5 py-4 font-semibold">
                    {item.source}
                  </div>
                  <div className="p-5">
                    <div className="text-sm text-gray-500 mb-1">Распознанные параметры</div>
                    <div className="text-gray-900 font-medium mb-4">{item.parsed}</div>
                    <div className="text-sm text-gray-500 mb-1">Что получает пользователь</div>
                    <div className="text-gray-900 font-medium">{item.result}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mb-12">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
                Как Get-Goods.ai ускоряет подбор
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Вместо копирования каждой строки в поисковики, каталоги поставщиков,
                прайсы и сайты производителей пользователь загружает спецификацию
                и получает структурированный результат по позициям.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {workflow.map((step) => (
                <div key={step.title} className="border border-gray-200 rounded-lg p-6 bg-white">
                  <step.icon className="h-10 w-10 text-blue-600 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                  Что на выходе
                </h2>
                <div className="space-y-4">
                  {page.outcomes.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">FAQ</h2>
                <div className="space-y-6">
                  {page.faq.map((item) => (
                    <div key={item.question}>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.question}</h3>
                      <p className="text-gray-600 leading-relaxed">{item.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-blue-600 text-white">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-5">
              Загрузите спецификацию и получите список позиций
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
              Get-Goods.ai помогает быстрее перейти от проектной строки к артикулам,
              брендам, характеристикам и ценам реальной электротехнической продукции.
            </p>
            <Link
              to={targetPath}
              onClick={() => trackSeoClick('product_pain_bottom_cta')}
              className="inline-flex items-center justify-center bg-white text-blue-600 hover:text-blue-700 px-8 py-4 rounded-lg text-base font-semibold transition-colors duration-300 shadow-lg"
            >
              {isAuthenticated ? 'Загрузить файл' : 'Попробовать подбор'}
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ProductPainSeoPage;
